import { useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { PropertyType } from "@/types/types";

const NIGERIAN_PROPERTY_TYPES: PropertyType[] = [
  "duplex",
  "bungalow",
  "self-contain",
  "mini-flat",
  "penthouse",
  "mansion",
  "boys-quarters",
  "detached",
  "semi-detached",
  "terrace",
  "flat",
  "studio",
  "apartment",
];

const NIGERIAN_STATES = [
  "Lagos",
  "Abuja",
  "Kano",
  "Ibadan",
  "Port Harcourt",
  "Benin City",
  "Kaduna",
  "Enugu",
  "Abeokuta",
  "Jos",
];

export interface PropertyFilters {
  search: string;
  types: PropertyType[];
  minPrice: number | null;
  maxPrice: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  location: string;
  sortBy: "price_asc" | "price_desc" | "newest" | "oldest";
}

interface PropertyFiltersComponentProps {
  filters: PropertyFilters;
  onFiltersChange: (filters: PropertyFilters) => void;
}

export default function PropertyFiltersComponent({
  filters,
  onFiltersChange,
}: PropertyFiltersComponentProps) {
  const [isOpen, setIsOpen] = useState(false);

  const updateFilters = (updates: Partial<PropertyFilters>) => {
    onFiltersChange({ ...filters, ...updates });
  };

  const togglePropertyType = (type: PropertyType) => {
    const types = filters.types.includes(type)
      ? filters.types.filter((t) => t !== type)
      : [...filters.types, type];
    updateFilters({ types });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      types: [],
      minPrice: null,
      maxPrice: null,
      bedrooms: null,
      bathrooms: null,
      location: "",
      sortBy: "newest",
    });
  };

  const activeFilterCount =
    (filters.types.length > 0 ? 1 : 0) +
    (filters.minPrice !== null ? 1 : 0) +
    (filters.maxPrice !== null ? 1 : 0) +
    (filters.bedrooms !== null ? 1 : 0) +
    (filters.bathrooms !== null ? 1 : 0) +
    (filters.location !== "" ? 1 : 0);

  return (
    <div className="space-y-4">
      {/* Search and Sort Bar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search properties..."
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
            className="pl-10"
          />
        </div>

        <Select
          value={filters.sortBy}
          onValueChange={(value: PropertyFilters["sortBy"]) =>
            updateFilters({ sortBy: value })
          }
        >
          <SelectTrigger className="w-full md:w-[200px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="price_asc">Price: Low to High</SelectItem>
            <SelectItem value="price_desc">Price: High to Low</SelectItem>
          </SelectContent>
        </Select>

        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Filter Properties</SheetTitle>
              <SheetDescription>
                Refine your search with these filters
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* Property Type */}
              <div>
                <Label className="text-base font-medium mb-3 block">
                  Property Type
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {NIGERIAN_PROPERTY_TYPES.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={type}
                        checked={filters.types.includes(type)}
                        onCheckedChange={() => togglePropertyType(type)}
                      />
                      <label
                        htmlFor={type}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize"
                      >
                        {type.replace("-", " ")}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Price Range */}
              <div>
                <Label className="text-base font-medium mb-3 block">
                  Price Range (₦)
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="minPrice" className="text-sm">
                      Min Price
                    </Label>
                    <Input
                      id="minPrice"
                      type="number"
                      placeholder="e.g., 100000"
                      value={filters.minPrice ?? ""}
                      onChange={(e) =>
                        updateFilters({
                          minPrice: e.target.value
                            ? Number(e.target.value)
                            : null,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="maxPrice" className="text-sm">
                      Max Price
                    </Label>
                    <Input
                      id="maxPrice"
                      type="number"
                      placeholder="e.g., 1000000"
                      value={filters.maxPrice ?? ""}
                      onChange={(e) =>
                        updateFilters({
                          maxPrice: e.target.value
                            ? Number(e.target.value)
                            : null,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Location */}
              <div>
                <Label
                  htmlFor="location"
                  className="text-base font-medium mb-3 block"
                >
                  Location
                </Label>
                <Select
                  value={filters.location}
                  onValueChange={(value) => updateFilters({ location: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Locations</SelectItem>
                    {NIGERIAN_STATES.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Bedrooms */}
              <div>
                <Label
                  htmlFor="bedrooms"
                  className="text-base font-medium mb-3 block"
                >
                  Bedrooms
                </Label>
                <Select
                  value={filters.bedrooms?.toString() ?? ""}
                  onValueChange={(value) =>
                    updateFilters({ bedrooms: value ? Number(value) : null })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any</SelectItem>
                    <SelectItem value="1">1+</SelectItem>
                    <SelectItem value="2">2+</SelectItem>
                    <SelectItem value="3">3+</SelectItem>
                    <SelectItem value="4">4+</SelectItem>
                    <SelectItem value="5">5+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Bathrooms */}
              <div>
                <Label
                  htmlFor="bathrooms"
                  className="text-base font-medium mb-3 block"
                >
                  Bathrooms
                </Label>
                <Select
                  value={filters.bathrooms?.toString() ?? ""}
                  onValueChange={(value) =>
                    updateFilters({ bathrooms: value ? Number(value) : null })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any</SelectItem>
                    <SelectItem value="1">1+</SelectItem>
                    <SelectItem value="2">2+</SelectItem>
                    <SelectItem value="3">3+</SelectItem>
                    <SelectItem value="4">4+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
                <Button className="flex-1" onClick={() => setIsOpen(false)}>
                  Apply Filters
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.types.map((type) => (
            <Button
              key={type}
              variant="secondary"
              size="sm"
              onClick={() => togglePropertyType(type)}
              className="gap-1"
            >
              {type.replace("-", " ")}
              <X className="h-3 w-3" />
            </Button>
          ))}
          {filters.location && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => updateFilters({ location: "" })}
              className="gap-1"
            >
              {filters.location}
              <X className="h-3 w-3" />
            </Button>
          )}
          {filters.bedrooms !== null && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => updateFilters({ bedrooms: null })}
              className="gap-1"
            >
              {filters.bedrooms}+ beds
              <X className="h-3 w-3" />
            </Button>
          )}
          {filters.bathrooms !== null && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => updateFilters({ bathrooms: null })}
              className="gap-1"
            >
              {filters.bathrooms}+ baths
              <X className="h-3 w-3" />
            </Button>
          )}
          {(filters.minPrice !== null || filters.maxPrice !== null) && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => updateFilters({ minPrice: null, maxPrice: null })}
              className="gap-1"
            >
              ₦{filters.minPrice ?? 0} - ₦{filters.maxPrice ?? "∞"}
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
